import {
  Box,
  CircularProgress,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import BookingCard from "app/components/BookingsAndReferrals/BookingCard";
import BookingsHeader from "app/components/BookingsAndReferrals/BookingsHeader";
import CalenderContainer from "app/components/BookingsAndReferrals/CalenderContainer";
import ReferralCard from "app/components/BookingsAndReferrals/ReferralCard";
import { BookingsAndReferralsTab } from "app/components/BookingsAndReferrals/types";
import NavBar from "app/components/NavBar";
import dayjs, { Dayjs } from "dayjs";
import {
  useLazyGetMyBookedReferralsQuery,
  useLazyGetMyBookingsQuery,
} from "lib/features/booking/bookingService";
import { Booking } from "lib/features/booking/types";
import { selectUser } from "lib/features/user/userSlice";
import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { bookingHomeStyles } from "styles/BookingReferralDirectory/bookingHome.styles";

const BookingsAndReferrals = () => {
  const theme = useTheme();
  const { id: businessId } = useParams();
  const user = useSelector(selectUser);

  const [activeTab, setActiveTab] = useState<string>("");
  const [markedDates, setMarkedDates] = useState<string[]>([]);
  const [dateChanged, setDateChanged] = useState<boolean>(false);
  const [selectedDate, setSelectedDate] = useState<Dayjs | null>(null);
  const [isCalendarVisible, setIsCalendarVisible] = useState<boolean>(false);

  const [searchQuery, setSearchQuery] = useState<string>("");

  const isScreenBreak = useMediaQuery("(max-width: 842px)");

  // For displaying filtered bookings
  const [getMyBookings, { data: myBookings, isLoading: isBookingsLoading }] =
    useLazyGetMyBookingsQuery();

  // For displaying filtered referrals
  const [
    getMyBookedReferrals,
    { data: myReferrals, isLoading: isReferralsLoading },
  ] = useLazyGetMyBookedReferralsQuery();

  // NEW: For getting ALL bookings/referrals to mark calendar dots
  const [getAllBookings, { data: allBookings }] = useLazyGetMyBookingsQuery();
  const [getAllReferrals, { data: allReferrals }] = useLazyGetMyBookedReferralsQuery();

  const filterBookings = (bookings: Booking[], query: string): Booking[] => {
    if (!query.trim()) return bookings;

    const searchTerm = query.toLowerCase().trim();

    return bookings.filter((booking) => {
      const itemName =
        booking?.farehabor_booking_details?.booking?.availability?.item?.name ||
        booking?.referral?.item?.itemDetails?.[0]?.item_name;

      const businessName =
        booking?.farehabor_booking_details?.booking?.company?.name ||
        booking?.referral?.business?.business;

      const customerName = booking?.customer_name;
      const customerEmail = booking?.customer_email;
      const customerPhone = booking?.customer_phone;

      const referrerFirstName =
        booking?.referral?.user?.first_name?.trim() || "";
      const referrerLastName = booking?.referral?.user?.last_name || "";
      const referrerFullName =
        `${referrerFirstName} ${referrerLastName}`.trim();

      return (
        itemName?.toLowerCase().includes(searchTerm) ||
        businessName?.toLowerCase().includes(searchTerm) ||
        customerName?.toLowerCase().includes(searchTerm) ||
        customerEmail?.toLowerCase().includes(searchTerm) ||
        customerPhone?.toLowerCase().includes(searchTerm) ||
        referrerFullName.toLowerCase().includes(searchTerm)
      );
    });
  };

  const filteredBookings = useMemo(() => {
    const bookingsArray = myBookings?.bookings || [];
    const filtered = filterBookings(bookingsArray, searchQuery);
    return [...filtered].sort((a, b) => {
      const dateA = dayjs(a.created_time);
      const dateB = dayjs(b.created_time);
      return dateB.valueOf() - dateA.valueOf();
    });
  }, [myBookings?.bookings, searchQuery]);

  const filteredReferrals = useMemo(() => {
    const referralsArray = myReferrals?.referrals || [];
    const filtered = filterBookings(referralsArray, searchQuery);
    return [...filtered].sort((a, b) => {
      const dateA = dayjs(a.created_time);
      const dateB = dayjs(b.created_time);
      return dateB.valueOf() - dateA.valueOf();
    });
  }, [myReferrals?.referrals, searchQuery]);

  const getReservationDatesFromBookings = (bookings: Booking[]): string[] => {
    if (!Array.isArray(bookings)) {
      return [];
    }

    const dates = bookings
      .map((booking) => {
        let reservationDate = null;
        
        if (booking?.farehabor_booking_details) {
          reservationDate = booking?.farehabor_booking_details?.booking?.availability?.start_at;
        } else {
          reservationDate = (booking as any)?.reservation_time;
        }

        if (!reservationDate) {
          console.warn('No reservation date found for booking:', booking?.id);
          return null;
        }

        return dayjs(reservationDate).format("YYYY-MM-DD");
      })
      .filter((date): date is string => date !== null);

    return Array.from(new Set(dates));
  };

  // Updated: Get marked dates from ALL bookings/referrals (not filtered ones)
  const getAllMarkedDates = () => {
    let newMarkedDates: string[] = [];

    if (activeTab === BookingsAndReferralsTab.Referrals) {
      const referralsArray = allReferrals?.referrals || [];
      newMarkedDates = getReservationDatesFromBookings(referralsArray);
    } else if (activeTab === BookingsAndReferralsTab.Bookings) {
      const bookingsArray = allBookings?.bookings || [];
      newMarkedDates = getReservationDatesFromBookings(bookingsArray);
    }

    setMarkedDates(newMarkedDates);
    console.log('Marked reservation dates for calendar:', newMarkedDates);
  };

  const handleDateChange = (newDate: Dayjs | null) => {
    if (newDate) {
      setSelectedDate(dayjs(newDate));
      setDateChanged(true);
    } else {
      setSelectedDate(null);
      setDateChanged(false);
    }
    setIsCalendarVisible(false);
  };

  useEffect(() => {
    setActiveTab(
      user?.role_id == 4
        ? BookingsAndReferralsTab.Bookings
        : BookingsAndReferralsTab.Referrals
    );
  }, [user?.role_id]);

  // Fetch filtered bookings (for display in cards)
  useEffect(() => {
    const fetchBookings = async () => {
      if (user?.role_id !== 4 || !businessId) return;

      try {
        await getMyBookings({
          business_id: Number(businessId),
          date: dateChanged ? selectedDate?.toDate() : undefined,
        }).unwrap();
      } catch (error) {
        console.error("Error fetching bookings:", error);
      }
    };

    fetchBookings();
  }, [selectedDate, user?.role_id, businessId, dateChanged, getMyBookings]);

  // Fetch filtered referrals (for display in cards)
  useEffect(() => {
    const fetchReferrals = async () => {
      if (!user?.id) return;

      try {
        await getMyBookedReferrals({
          user_id: Number(user?.id),
          date: dateChanged ? selectedDate?.toDate() : undefined,
        }).unwrap();
      } catch (error) {
        console.error("Error fetching referrals:", error);
      }
    };

    fetchReferrals();
  }, [selectedDate, user?.id, dateChanged, getMyBookedReferrals]);

  // NEW: Fetch ALL bookings for calendar dots (no date filter)
  useEffect(() => {
    const fetchAllBookings = async () => {
      if (user?.role_id !== 4 || !businessId) return;

      try {
        await getAllBookings({
          business_id: Number(businessId),
          // No date parameter - get all bookings
        }).unwrap();
      } catch (error) {
        console.error("Error fetching all bookings for calendar:", error);
      }
    };

    fetchAllBookings();
  }, [user?.role_id, businessId, getAllBookings]);

  // NEW: Fetch ALL referrals for calendar dots (no date filter)
  useEffect(() => {
    const fetchAllReferrals = async () => {
      if (!user?.id) return;

      try {
        await getAllReferrals({
          user_id: Number(user?.id),
          // No date parameter - get all referrals
        }).unwrap();
      } catch (error) {
        console.error("Error fetching all referrals for calendar:", error);
      }
    };

    fetchAllReferrals();
  }, [user?.id, getAllReferrals]);

  // Update marked dates when ALL bookings/referrals change
  useEffect(() => {
    getAllMarkedDates();
  }, [allBookings, allReferrals, activeTab]);

  return (
    <>
      <NavBar />
      <Box sx={bookingHomeStyles.mainContainer(theme)}>
        {/* Header */}
        <BookingsHeader
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          businessUser={user?.role_id === 4}
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
        />

        {/* Calendar */}
        <CalenderContainer
          dateChanged={dateChanged}
          selectedDate={selectedDate}
          handleDateChange={handleDateChange}
          isCalendarVisible={isCalendarVisible}
          setIsCalendarVisible={setIsCalendarVisible}
          markedDates={markedDates}
        />

        {/* Cards */}
        {isBookingsLoading || isReferralsLoading ? (
          <Box sx={bookingHomeStyles.loaderContainer}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {activeTab === BookingsAndReferralsTab.Bookings &&
              (filteredBookings.length === 0 ? (
                <Typography sx={bookingHomeStyles.noRecordText}>
                  {searchQuery
                    ? "No bookings found matching your search."
                    : "No Bookings found."}
                </Typography>
              ) : (
                <Box sx={bookingHomeStyles.cardsContainer(isScreenBreak)}>
                  {filteredBookings.map((booking: Booking) => (
                    <BookingCard booking={booking} key={booking?.id} />
                  ))}
                </Box>
              ))}

            {activeTab === BookingsAndReferralsTab.Referrals &&
              (filteredReferrals.length === 0 ? (
                <Typography sx={bookingHomeStyles.noRecordText}>
                  {searchQuery
                    ? "No referrals found matching your search."
                    : "No Referrals found."}
                </Typography>
              ) : (
                <Box sx={bookingHomeStyles.cardsContainer(isScreenBreak)}>
                  {filteredReferrals?.map((referral: Booking) => (
                    <ReferralCard referral={referral} key={referral?.id} />
                  ))}
                </Box>
              ))}
          </>
        )}
      </Box>
    </>
  );
};

export default BookingsAndReferrals;